import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Circle,
  Play,
  Download,
  Share2,
  Bookmark,
  BookmarkCheck,
  FileText,
  Globe,
  Smartphone,
  GraduationCap,
  Printer,
  Image,
  Music,
  Headphones,
  Wrench,
  Gamepad2,
  BookOpen,
  Check,
  ExternalLink,
  Award,
  AlertCircle,
  Clock,
  ShoppingCart,
  List,
  Layout,
  ArrowLeft,
  MessageSquare,
  Users,
  Star,
  User,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getCourseById, getCourseContent } from "@/admin/utils/courseService";
import {
  getCourseCache,
  setCourseCache,
  getCourseContentCache,
  setCourseContentCache,
  shouldRefreshCache,
  clearCourseCache,
} from "@/utils/cacheService";
import {
  getUserCourseProgress,
  updateUserProgress,
  mergeProgressWithLocalStorage,
} from "@/services/progressService";
import {
  getUserCertificate,
  createCertificate,
  hasCertificateForCourse,
} from "@/services/certificateService";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { QRCodeSVG } from "qrcode.react";
import { ExpertField } from "@/models/Expert";
import { supabase } from "@/lib/supabaseClient";
import LessonContentView from "@/components/courses/LessonContentView";
import ContentChatWidget from "@/components/ai/ContentChatWidget";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@/components/ui/breadcrumb";
import { decodeUnicodeString } from "@/utils/textUtils";
import CoursePdfExport from "@/components/courses/CoursePdfExport";
import { SEO } from "@/components/ui/seo";
import { Helmet } from "react-helmet-async";
import { Spinner } from "@/components/ui/spinner";
import { getCategoryColors } from '@/lib/category-colors';
import { cn } from '@/lib/utils';
import { Course } from '@/types/course';
import { getCourseDetail } from '@/api/courseHandler';

// Hàm chuẩn hóa cấu trúc nội dung khóa học để đảm bảo nhất quán
const normalizeContentStructure = (content: any): any => {
  // Nếu content là null hoặc undefined, trả về một object rỗng an toàn
  if (!content) {
    console.warn("Content là null hoặc undefined");
    return { nodes: [] };
  }

  console.log("Đang chuẩn hóa nội dung khóa học, loại content:", typeof content);
  
  // Xử lý trường hợp content là string (có thể là JSON string)
  if (typeof content === "string") {
    try {
      const parsedContent = JSON.parse(content);
      console.log("Đã parse content từ string thành:", typeof parsedContent);

      // Nếu đã parse được và là mảng, thì đó là mảng nodes
      if (Array.isArray(parsedContent)) {
        console.log("Content là JSON string chứa mảng nodes, đang chuẩn hóa");
        return { nodes: parsedContent };
      }

      // Nếu đã parse được và là object có nodes
      if (parsedContent && parsedContent.nodes) {
        console.log("Content là JSON string chứa object có nodes");
        return parsedContent;
      }
      
      // Kiểm tra cấu trúc JSON mới (new_course.json) - nếu có content bên trong và content có nodes
      if (parsedContent && parsedContent.content && parsedContent.content.nodes) {
        console.log("Content có cấu trúc mới với nodes nằm trong content.nodes");
        return parsedContent.content;
      }
      
      // Trường hợp đặc biệt cho new_course.json khi content ở ngoài
      if (parsedContent && !parsedContent.nodes && parsedContent.content && 
          typeof parsedContent.content === 'object' && parsedContent.content.nodes) {
        console.log("Content có cấu trúc đặc biệt với nodes nằm trong parsedContent.content.nodes");
        return parsedContent.content;
      }

      // Kiểm tra thêm trường hợp từ example-course-ai-generate.sql
      if (parsedContent && !parsedContent.nodes && parsedContent.id && Array.isArray(parsedContent.tags)) {
        console.log("Content có vẻ là cấu trúc khóa học đầy đủ nhưng nodes không nằm trong thuộc tính nodes");
        return parsedContent;
      }

      // Trường hợp khác, wrap lại trong một object với thuộc tính nodes
      console.log(
        "Content là JSON string nhưng không có cấu trúc chuẩn, wrap lại"
      );
      return { nodes: parsedContent };
    } catch (e) {
      // Nếu không parse được, giả định là một node duy nhất
      console.warn("Không thể parse content dạng string thành JSON:", e);
      return { nodes: [] };
    }
  }

  // Nếu content đã là mảng, wrap lại trong một object
  if (Array.isArray(content)) {
    console.log("Content là mảng nodes, đang chuẩn hóa");
    return { nodes: content };
  }

  // Nếu content là object nhưng không có nodes
  if (typeof content === "object" && !content.nodes) {
    // Kiểm tra nếu content.content có tồn tại và có nodes - cấu trúc new_course.json
    if (content.content && content.content.nodes) {
      console.log("Content chứa trong content.content, đang trích xuất");
      return content.content;
    }
    
    // Trường hợp cho cấu trúc của new_course.json - nodes nằm trực tiếp trong content
    if (Array.isArray(content.nodes)) {
      console.log("Content có nodes trực tiếp, đang trả về content");
      return content;
    }

    // Kiểm tra trường hợp từ example-course-ai-generate.sql
    if (content.id && Array.isArray(content.tags) && Array.isArray(content.nodes)) {
      console.log("Content có cấu trúc theo example-course-ai-generate.sql");
      return content;
    }

    // Trường hợp không có cấu trúc rõ ràng, có thể là một node
    // Kiểm tra nếu content có trường id và name thì coi nó là một node
    if (content.id && content.name) {
      console.log("Content là một node đơn lẻ, wrap lại trong mảng");
      return { nodes: [content] };
    }

    // Trường hợp khác, trả về object với mảng nodes rỗng
    console.warn("Content không có cấu trúc chuẩn");
    return { nodes: [] };
  }

  // Nếu content đã có cấu trúc chuẩn (có thuộc tính nodes)
  console.log("Content đã có cấu trúc chuẩn");
  return content;
};

type NodeProgress = {
  [key: string]: boolean;
};

// Component thẻ node hiển thị trong danh sách các bài học
const NodeCard = ({
  node,
  isActive,
  onClick,
  isCompleted,
}: {
  node: any;
  isActive: boolean;
  onClick: () => void;
  isCompleted: boolean;
}) => (
  <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
    <Card
      className={`cursor-pointer border-none shadow-md hover:shadow-lg transition-all duration-300 mb-4 ${
        isActive ? "ring-2 ring-indigo-500" : ""
      }`}
      onClick={onClick}
    >
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div className="flex items-center">
          {isCompleted ? (
            <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
          ) : (
            <Circle className="h-5 w-5 text-muted-foreground mr-2" />
          )}
          <CardTitle className="text-lg">{node.name}</CardTitle>
        </div>
        {isActive && (
          <Badge variant="outline" className="bg-indigo-100">
            Đang xem
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        <CardDescription>{node.description}</CardDescription>
      </CardContent>
    </Card>
  </motion.div>
);

// Component hiển thị nội dung của node
const ContentView = ({ node }: { node: any }) => {
  const [bookmarked, setBookmarked] = useState(false);

  // Kiểm tra nếu nội dung đã được đánh dấu
  useEffect(() => {
    const bookmarkedItems = JSON.parse(
      localStorage.getItem("bookmarked_items") || "[]",
    );
    const isBookmarked = bookmarkedItems.some(
      (item: any) => item.id === node.id,
    );
    setBookmarked(isBookmarked);
  }, [node]);

  // Xử lý đánh dấu nội dung
  const handleBookmark = () => {
    const bookmarkedItems = JSON.parse(
      localStorage.getItem("bookmarked_items") || "[]",
    );

    if (bookmarked) {
      // Bỏ đánh dấu
      const updatedItems = bookmarkedItems.filter(
        (item: any) => item.id !== node.id,
      );
      localStorage.setItem("bookmarked_items", JSON.stringify(updatedItems));
      setBookmarked(false);
    } else {
      // Thêm đánh dấu
      const newItem = {
        id: node.id,
        name: node.name,
        type: "course_node",
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(
        "bookmarked_items",
        JSON.stringify([...bookmarkedItems, newItem]),
      );
      setBookmarked(true);
    }
  };

  // Hiển thị biểu tượng theo loại tài liệu tham khảo
  const getResourceIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Play className="h-5 w-5 text-red-500" />;
      case "article":
        return <FileText className="h-5 w-5 text-blue-500" />;
      case "pdf":
        return <FileText className="h-5 w-5 text-red-500" />;
      case "ebook":
        return <BookOpen className="h-5 w-5 text-green-500" />;
      case "website":
        return <Globe className="h-5 w-5 text-purple-500" />;
      case "app":
        return <Smartphone className="h-5 w-5 text-indigo-500" />;
      case "course":
        return <GraduationCap className="h-5 w-5 text-amber-500" />;
      case "printable":
        return <Printer className="h-5 w-5 text-cyan-500" />;
      case "template":
        return <FileText className="h-5 w-5 text-teal-500" />;
      case "infographic":
        return <Image className="h-5 w-5 text-pink-500" />;
      case "audio":
        return <Music className="h-5 w-5 text-violet-500" />;
      case "podcast":
        return <Headphones className="h-5 w-5 text-fuchsia-500" />;
      case "tool":
        return <Wrench className="h-5 w-5 text-emerald-500" />;
      case "game":
        return <Gamepad2 className="h-5 w-5 text-rose-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  // Lấy nhãn hiển thị cho loại node
  const getNodeTypeLabel = (type: string) => {
    switch (type) {
      case "concept":
        return "Khái niệm";
      case "practice":
        return "Thực hành";
      case "activity":
        return "Hoạt động";
      case "lesson":
        return "Bài học";
      case "quiz":
        return "Bài kiểm tra";
      default:
        return "Nội dung";
    }
  };

  // Kiểm tra xem nội dung có phải là HTML hay không
  const isHtmlContent = (content: string): boolean => {
    if (!content) return false;

    // Kiểm tra nếu nội dung có chứa thẻ HTML
    if (content.trim().startsWith("<") && content.trim().endsWith(">")) {
      return true;
    }

    // Kiểm tra nếu nội dung chứa HTML được escape
    if (content.includes("&lt;") && content.includes("&gt;")) {
      return true;
    }

    return false;
  };

  // Xử lý nội dung HTML bị escape
  const processHtmlContent = (content: string): string => {
    if (!content) return "";

    // Nếu nội dung bị escape (chứa &lt; và &gt;)
    if (content.includes("&lt;") && content.includes("&gt;")) {
      const textarea = document.createElement("textarea");
      textarea.innerHTML = content;
      return textarea.value;
    }

    return content;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 lg:p-8">
      <div className="flex justify-between items-center mb-6">
        <Badge variant="outline" className="text-xs capitalize">
          {getNodeTypeLabel(node.type)}
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBookmark}
          className="text-muted-foreground hover:text-indigo-500"
        >
          {bookmarked ? (
            <BookmarkCheck className="h-5 w-5 text-indigo-500" />
          ) : (
            <Bookmark className="h-5 w-5" />
          )}
        </Button>
      </div>

      <h2 className="text-2xl font-bold mb-4">{node.name}</h2>
      <p className="text-muted-foreground mb-6">{node.description}</p>

      <div className="prose max-w-none">
        {isHtmlContent(node.content) ? (
          <div
            className="lesson-content"
            dangerouslySetInnerHTML={{
              __html: processHtmlContent(node.content || ""),
            }}
          />
        ) : (
          <div
            dangerouslySetInnerHTML={{
              __html: processMarkdownContent(node.content || ""),
            }}
          ></div>
        )}
      </div>

      {node.resources && node.resources.length > 0 && (
        <div className="mt-8 p-6 bg-slate-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Download className="h-5 w-5 text-blue-500 mr-2" />
            Tài liệu tham khảo
          </h3>
          <div className="space-y-4">
            {node.resources.map((resource: any, index: number) => (
              <div key={index} className="flex items-start">
                <div className="mr-3 mt-1">
                  {getResourceIcon(resource.type)}
                </div>
                <div className="flex-1">
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline block"
                  >
                    {resource.title}
                  </a>
                  <p className="text-sm text-muted-foreground">
                    {resource.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {node.type === "practice" && (
        <div className="mt-8 p-6 bg-indigo-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Play className="h-5 w-5 text-indigo-500 mr-2" />
            Thực hành ngay
          </h3>
          <p className="mb-4">
            Hãy thử áp dụng những nguyên tắc này vào cuộc sống hàng ngày của
            bạn:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Dành thời gian mỗi ngày để thực hành</li>
            <li>Ghi lại những thay đổi bạn nhận thấy</li>
            <li>Điều chỉnh phương pháp cho phù hợp với hoàn cảnh cá nhân</li>
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button className="bg-indigo-500 hover:bg-indigo-600">
              <Share2 className="h-4 w-4 mr-2" /> Chia sẻ
            </Button>
          </div>
        </div>
      )}

      {node.type === "activity" && (
        <div className="mt-8 p-6 bg-blue-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Play className="h-5 w-5 text-blue-500 mr-2" />
            Hoạt động tương tác
          </h3>
          <p className="mb-4">
            Đây là hoạt động mà bạn có thể thực hiện để áp dụng kiến thức:
          </p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Chuẩn bị các vật dụng cần thiết</li>
            <li>Dành thời gian không bị gián đoạn</li>
            <li>Ghi chép lại quá trình thực hiện</li>
            <li>Chia sẻ kết quả với người khác</li>
          </ol>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button className="bg-blue-500 hover:bg-blue-600">
              <Share2 className="h-4 w-4 mr-2" /> Chia sẻ
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Hàm ánh xạ danh mục khóa học sang lĩnh vực chuyên gia
const mapCategoryToField = (category?: string) => {
  // Ánh xạ danh mục sang lĩnh vực chuyên gia
  const categoryToFieldMap: Record<string, ExpertField> = {
    buddhism: ExpertField.BUDDHISM,
    meditation: ExpertField.MEDITATION,
    philosophy: ExpertField.PHILOSOPHY,
    psychology: ExpertField.PSYCHOLOGY,
    health: ExpertField.HEALTH,
    nutrition: ExpertField.NUTRITION,
    parenting: ExpertField.PARENTING,
    education: ExpertField.EDUCATION,
    science: ExpertField.SCIENCE,
    history: ExpertField.HISTORY,
    arts: ExpertField.ARTS,
    literature: ExpertField.LITERATURE,
    technology: ExpertField.TECHNOLOGY,
    business: ExpertField.BUSINESS,
    finance: ExpertField.FINANCE,
    environment: ExpertField.ENVIRONMENT,
  };

  return category
    ? categoryToFieldMap[category] || ExpertField.GENERAL
    : ExpertField.GENERAL;
};

// Xử lý markdown đơn giản bằng regex
const processMarkdownContent = (content: string): string => {
  if (!content) return "";

  let processedContent = content;

  // Xử lý các tiêu đề (headers)
  // H1: # Tiêu đề
  processedContent = processedContent.replace(
    /^# (.+)$/gm,
    '<h1 class="text-3xl font-bold my-4">$1</h1>',
  );
  // H2: ## Tiêu đề
  processedContent = processedContent.replace(
    /^## (.+)$/gm,
    '<h2 class="text-2xl font-bold my-3">$1</h2>',
  );
  // H3: ### Tiêu đề
  processedContent = processedContent.replace(
    /^### (.+)$/gm,
    '<h3 class="text-xl font-bold my-2">$1</h3>',
  );
  // H4: #### Tiêu đề
  processedContent = processedContent.replace(
    /^#### (.+)$/gm,
    '<h4 class="text-lg font-bold my-2">$1</h4>',
  );
  // H5: ##### Tiêu đề
  processedContent = processedContent.replace(
    /^##### (.+)$/gm,
    '<h5 class="text-base font-bold my-1">$1</h5>',
  );
  // H6: ###### Tiêu đề
  processedContent = processedContent.replace(
    /^###### (.+)$/gm,
    '<h6 class="text-sm font-bold my-1">$1</h6>',
  );

  // Xử lý in đậm (**text** hoặc __text__)
  processedContent = processedContent.replace(
    /\*\*(.*?)\*\*|__(.*?)__/g,
    "<strong>$1$2</strong>",
  );

  // Xử lý in nghiêng (*text* hoặc _text_)
  processedContent = processedContent.replace(
    /\*(.*?)\*|_(.*?)_/g,
    "<em>$1$2</em>",
  );

  // Xử lý gạch ngang (~~ text ~~)
  processedContent = processedContent.replace(/~~(.*?)~~/g, "<del>$1</del>");

  // Xử lý danh sách không thứ tự
  processedContent = processedContent.replace(
    /^\s*[-*+]\s+(.+)$/gm,
    "<li>$1</li>",
  );
  // Nhóm các <li> thành <ul>
  processedContent = processedContent.replace(
    /(<li>.*?<\/li>)\s*\n\s*(?!<li>)/gs,
    '<ul class="list-disc pl-5 my-2">$1</ul>\n',
  );

  // Xử lý danh sách có thứ tự
  processedContent = processedContent.replace(
    /^\s*(\d+)\.\s+(.+)$/gm,
    "<li>$2</li>",
  );
  // Nhóm các <li> có thứ tự thành <ol>
  processedContent = processedContent.replace(
    /(<li>.*?<\/li>)\s*\n\s*(?!<li>)/gs,
    '<ol class="list-decimal pl-5 my-2">$1</ol>\n',
  );

  // Xử lý link [text](url)
  processedContent = processedContent.replace(
    /\[(.*?)\]\((.*?)\)/g,
    '<a href="$2" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>',
  );

  // Xử lý blockquote
  processedContent = processedContent.replace(
    /^>\s+(.+)$/gm,
    '<blockquote class="border-l-4 border-gray-300 pl-4 my-2 italic">$1</blockquote>',
  );

  // Xử lý xuống dòng (2 dấu cách cuối dòng hoặc dòng trống)
  processedContent = processedContent.replace(/  $/gm, "<br />");
  processedContent = processedContent.replace(/\n\n/g, '<p class="my-2"></p>');

  // Xử lý code inline
  processedContent = processedContent.replace(
    /`([^`]+)`/g,
    '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>',
  );

  // Xử lý code block (cơ bản, không hỗ trợ highlighting)
  processedContent = processedContent.replace(
    /```(?:\w+)?\n([\s\S]+?)\n```/g,
    '<pre class="bg-gray-100 p-3 rounded my-3 overflow-x-auto font-mono text-sm">$1</pre>',
  );

  return processedContent;
};

// Component trang chi tiết khóa học
const CourseDetailPage = () => {
  const { id } = useParams();
  const { t } = useTranslation(["courses", "translation"]);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [course, setCourse] = useState<Course | null>(null);
  const [courseContent, setCourseContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [nodeProgress, setNodeProgress] = useState<NodeProgress>({});
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
  const [isPurchased, setIsPurchased] = useState<boolean>(false);
  const [purchaseInProgress, setPurchaseInProgress] = useState<boolean>(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificateData, setCertificateData] = useState<any>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareableLink, setShareableLink] = useState("");
  const [userCredits, setUserCredits] = useState(0);
  const [showPurchaseConfirm, setShowPurchaseConfirm] = useState(false);
  const creditInputRef = useRef<HTMLInputElement>(null);
  const [viewMode, setViewMode] = useState<"list" | "card">("card");
  const [isMobile, setIsMobile] = useState(false);
  const [showLessonContent, setShowLessonContent] = useState(false);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [error, setError] = useState('');
  const [isLessonViewReady, setIsLessonViewReady] = useState(false);

  // Lấy thông tin khóa học
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        setError('');
        
        const data = await getCourseDetail(id!);
        setCourse(data);
      } catch (err: any) {
        console.error('Error fetching course:', err);
        setError(err.message || 'Đã xảy ra lỗi khi tải khóa học');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchCourse();
    }
  }, [id]);

  // Hàm quay lại
  const goBack = () => {
    navigate(-1);
  };

  // Kiểm tra thiết bị và cập nhật viewMode phù hợp
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768;
      setIsMobile(isMobileDevice);

      // Tự động chuyển sang chế độ list cho mobile nếu chưa có preference
      if (isMobileDevice) {
        const savedViewMode = localStorage.getItem("course_view_mode");
        if (!savedViewMode) {
          setViewMode("list");
        }
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    // Lấy preference từ localStorage nếu có
    const savedViewMode = localStorage.getItem("course_view_mode");
    if (savedViewMode === "list" || savedViewMode === "card") {
      setViewMode(savedViewMode);
    }

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Lưu preference mỗi khi thay đổi
  useEffect(() => {
    localStorage.setItem("course_view_mode", viewMode);
  }, [viewMode]);

  // Check if course is free and automatically set it as purchased and is_free flag
  useEffect(() => {
    if (course) {
      // Set is_free flag based on price
      if (course.price === 0) {
        setCourse((prevCourse) => ({ ...prevCourse, is_free: true }));
        setIsPurchased(true);
      } else {
        // Ensure is_free is false for paid courses
        setCourse((prevCourse) => ({ ...prevCourse, is_free: false }));
      }
    }
  }, [course?.price]);

  // Debug: Theo dõi thay đổi của state course
  useEffect(() => {
    if (course) {
      console.log(
        "Course state changed:",
        course.id,
        "price:",
        course.price,
        "type:",
        typeof course.price,
      );
    }
  }, [course]);

  // Kiểm tra trạng thái đã mua khóa học
  const checkPurchaseStatus = async () => {
    if (!authLoading && user && id) {
      try {
        // Kiểm tra trong bảng user_course_purchases
        const { data, error } = await supabase
          .from("user_course_purchases")
          .select("*")
          .eq("user_id", user.id)
          .eq("course_id", id)
          .limit(1);

        if (error) {
          console.error("Error checking purchase status:", error);
          return false;
        }

        // Nếu có dữ liệu, nghĩa là đã mua
        if (data && data.length > 0) {
          console.log("Course already purchased:", data[0]);
          setIsPurchased(true);
          return true;
        } else {
          setIsPurchased(false);
          return false;
        }
      } catch (error) {
        console.error("Error in checkPurchaseStatus:", error);
        return false;
      }
    }
  };

  // Ghi nhận việc mua khóa học vào database
  const recordPurchase = async () => {
    if (!authLoading && user && id && course) {
      try {
        // Thêm bản ghi vào bảng user_course_purchases
        const { data, error } = await supabase
          .from("user_course_purchases")
          .insert({
            user_id: user.id,
            course_id: id,
            purchase_amount: course.price,
            purchase_date: new Date().toISOString(),
            is_active: true,
            payment_method: "ai_credits",
            payment_status: "completed",
            access_expiry: null, // Null có nghĩa là không hết hạn
          });

        if (error) {
          console.error("Error recording purchase:", error);
          return false;
        }

        // Cập nhật state isPurchased
        setIsPurchased(true);
        return true;
      } catch (error) {
        console.error("Error in recordPurchase:", error);
        return false;
      }
    }
  };

  // Tải dữ liệu khóa học và nội dung
  useEffect(() => {
    const fetchCourseData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError('');
        
        console.log("Fetching course details for ID:", id);
        const data = await getCourseDetail(id);
        if (!data) {
          throw new Error('Không tìm thấy khóa học');
        }
        
        console.log("Course data received:", data);
        setCourse(data);
        
        // Nếu có content trong data thì set luôn
        if (data.content) {
          console.log("Course content found in data:", data.content);
          const normalizedContent = normalizeContentStructure(data.content);
          console.log("Normalized content:", normalizedContent);
          setCourseContent(normalizedContent);
        } else {
          console.log("No content found in course data, trying to fetch from course_contents");
          try {
            // Thử lấy nội dung từ bảng course_contents
            const { data: contentData, error } = await supabase
              .from('course_contents')
              .select('content')
              .eq('course_id', id)
              .single();
              
            if (error) {
              console.error("Error fetching from course_contents:", error);
            } else if (contentData && contentData.content) {
              console.log("Content fetched from course_contents:", contentData.content);
              const normalizedContent = normalizeContentStructure(contentData.content);
              console.log("Normalized content:", normalizedContent);
              setCourseContent(normalizedContent);
            } else {
              console.log("No content found in course_contents");
            }
          } catch (contentError) {
            console.error("Error in content fetch:", contentError);
          }
        }
      } catch (err: any) {
        console.error('Error fetching course:', err);
        setError(err.message || 'Đã xảy ra lỗi khi tải khóa học');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchCourseData();
    }
  }, [id]);

  // Xử lý khi đóng nội dung bài học
  const handleCloseLessonContent = () => {
    setShowLessonContent(false);
    setSelectedNode(null);
    setActiveNodeId(null);

    // Quay lại trang chi tiết khóa học
    if (id) {
      navigate(`/courses/${id}`);
    }
  };

  // Xử lý khi chuyển đến bài học trước đó
  const handlePrevLesson = () => {
    if (!selectedNode || !courseContent) return;

    const currentIndex = courseContent.nodes.findIndex(
      (node) => node.id === selectedNode.id,
    );
    if (currentIndex > 0) {
      const prevNode = courseContent.nodes[currentIndex - 1];
      handleViewLesson(prevNode);
    }
  };

  // Xử lý khi chuyển đến bài học tiếp theo
  const handleNextLesson = () => {
    if (!selectedNode || !courseContent) return;

    const currentIndex = courseContent.nodes.findIndex(
      (node) => node.id === selectedNode.id,
    );
    if (currentIndex < courseContent.nodes.length - 1) {
      // Đánh dấu bài học hiện tại là đã hoàn thành
      if (selectedNode.id && !nodeProgress[selectedNode.id]) {
        const newProgress = {
          ...nodeProgress,
          [selectedNode.id]: true,
        };
        setNodeProgress(newProgress);
        // Lưu tiến độ vào cơ sở dữ liệu
        saveProgress(newProgress);
      }

      const nextNode = courseContent.nodes[currentIndex + 1];
      handleViewLesson(nextNode);
    }
  };

  // Kiểm tra có bài học trước không
  const hasPrevLesson = () => {
    if (!selectedNode || !courseContent) return false;

    const currentIndex = courseContent.nodes.findIndex(
      (node) => node.id === selectedNode.id,
    );
    return currentIndex > 0;
  };

  // Kiểm tra có bài học tiếp theo không
  const hasNextLesson = () => {
    if (!selectedNode || !courseContent) return false;

    const currentIndex = courseContent.nodes.findIndex(
      (node) => node.id === selectedNode.id,
    );
    return currentIndex < courseContent.nodes.length - 1;
  };

  // Xử lý khi xem bài học
  const handleViewLesson = (node: any) => {
    console.log("handleViewLesson called with node:", node);
    setSelectedNode(node);
    setShowLessonContent(true);
    setActiveNodeId(node.id);

    // Điều hướng đến trang bài học
    if (id) {
      const lessonUrl = `/courses/${id}/lessons/${node.id}`;
      console.log("Navigating to:", lessonUrl);
      navigate(lessonUrl);
    }

    // Cập nhật tiến độ
    if (node.id && !nodeProgress[node.id]) {
      const newProgress = { ...nodeProgress };
      newProgress[node.id] = true;
      setNodeProgress(newProgress);

      // Lưu tiến độ học tập vào localStorage
      localStorage.setItem(`course_progress_${id}`, JSON.stringify(newProgress));
    }
  };

  // Kiểm tra URL khi component mount để hiển thị bài học
  useEffect(() => {
    const checkUrlForLesson = () => {
      const path = window.location.pathname;
      const match = path.match(/\/courses\/(.+?)\/lesson\/(.+)$/);
      
      console.log("Checking URL for lesson path:", path);
      
      if (match && match[2]) {
        const [, pathCourseId, pathLessonId] = match;
        console.log("URL path match:", { pathCourseId, pathLessonId });
        
        if (pathCourseId === id && courseContent) {
          console.log("CourseContent in state:", courseContent);
          if (courseContent.nodes) {
            console.log("Available nodes:", courseContent.nodes.map((n: any) => ({ id: n.id, name: n.name })));
          } else {
            console.log("courseContent doesn't have nodes property, full content:", courseContent);
          }
          
          let targetNode = null;
          
          // Tìm node trong courseContent.nodes
          if (courseContent.nodes) {
            targetNode = courseContent.nodes.find((n: any) => n.id === pathLessonId);
            console.log("Search result in nodes:", targetNode ? "Found" : "Not found");
          }
          
          // Thử tìm trong mảng nếu courseContent là mảng
          if (!targetNode && Array.isArray(courseContent)) {
            targetNode = courseContent.find((n: any) => n.id === pathLessonId);
            console.log("Search result in array:", targetNode ? "Found" : "Not found");
          }
          
          if (targetNode) {
            console.log("Found node:", targetNode.name);
            setSelectedNode(targetNode);
            setShowLessonContent(true);
            setActiveNodeId(targetNode.id);
          } else {
            console.error("Lesson node not found:", pathLessonId);
          }
        } else {
          console.log("Either courseId doesn't match or courseContent is not yet loaded");
        }
      } else {
        console.log("No lesson path detected in URL");
      }
    };
    
    // Chạy kiểm tra này sau một khoảng thời gian ngắn để đảm bảo courseContent đã được tải
    const timeoutId = setTimeout(checkUrlForLesson, 500);
    
    return () => clearTimeout(timeoutId);
  }, [id, courseContent]);

  // Tính toán phần trăm hoàn thành
  const calculateProgress = () => {
    if (!courseContent?.nodes?.length) return 0;
    const completedNodes = Object.values(nodeProgress).filter(Boolean).length;
    return Math.round((completedNodes / courseContent.nodes.length) * 100);
  };

  // Kiểm tra đã hoàn thành khóa học chưa
  const isFullyCompleted = () => {
    return calculateProgress() === 100;
  };

  // Reset tiến độ học tập
  const resetProgress = () => {
    setNodeProgress({});
    localStorage.removeItem(`course_progress_${id}`);
  };

  // Xử lý chia sẻ khóa học
  const handleShare = () => {
    const shareUrl = window.location.origin + `/courses/${id}`;
    setShareableLink(shareUrl);
    setShowShareDialog(true);
  };

  // Xử lý đăng ký khóa học miễn phí
  const handleEnrollFreeCourse = async () => {
    if (!user) {
      // Redirect to login
      navigate('/login', { state: { from: `/courses/${id}` } });
      return;
    }

    setPurchaseInProgress(true);
    try {
      await recordPurchase();
      setIsPurchased(true);
        toast({
        title: "Đăng ký thành công",
        description: "Bạn đã đăng ký khóa học thành công",
      });
    } catch (error) {
      console.error('Error enrolling in course:', error);
      toast({
        title: "Lỗi đăng ký",
        description: "Đã có lỗi xảy ra khi đăng ký khóa học",
        variant: "destructive",
      });
    } finally {
      setPurchaseInProgress(false);
    }
  };

  // Xử lý xác nhận mua khóa học
  const handleConfirmPurchase = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/courses/${id}` } });
          return;
        }

    setPurchaseInProgress(true);
    try {
      await recordPurchase();
        setIsPurchased(true);
      setShowPurchaseConfirm(false);
        toast({
          title: "Mua khóa học thành công",
        description: "Bạn đã mua khóa học thành công",
      });
      } catch (error) {
      console.error('Error purchasing course:', error);
        toast({
        title: "Lỗi mua khóa học",
        description: "Đã có lỗi xảy ra khi mua khóa học",
          variant: "destructive",
        });
      } finally {
        setPurchaseInProgress(false);
    }
  };

  // Xử lý hiển thị chứng chỉ
  const handleShowCertificate = async () => {
    try {
      // Kiểm tra và tạo chứng chỉ nếu chưa có
      const hasCertificate = await hasCertificateForCourse(user?.id || '', id || '');
      
      if (!hasCertificate) {
        await createCertificate({
          userId: user?.id || '',
          courseId: id || '',
          completionDate: new Date().toISOString(),
        });
      }
      
      // Lấy thông tin chứng chỉ
      const certificate = await getUserCertificate(user?.id || '', id || '');
      setCertificateData(certificate);
      setShowCertificate(true);
    } catch (error) {
      console.error('Error handling certificate:', error);
      toast({
        title: "Lỗi chứng chỉ",
        description: "Đã có lỗi xảy ra khi xử lý chứng chỉ",
        variant: "destructive",
      });
    }
  };

  // Kiểm tra khi selectedNode thay đổi
  useEffect(() => {
    if (selectedNode) {
      console.log("Selected node changed:", selectedNode);
      // Đợi 300ms để đảm bảo dữ liệu đã sẵn sàng trước khi render
      const timer = setTimeout(() => {
        setIsLessonViewReady(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setIsLessonViewReady(false);
    }
  }, [selectedNode]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!course || !courseContent) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-6 max-w-2xl mx-auto">
          <h2 className="text-xl font-bold mb-4">Không tìm thấy khóa học</h2>
          <p>Khóa học này không tồn tại hoặc đã bị xóa.</p>
          <Button className="mt-6" onClick={goBack}>
            <ChevronLeft className="h-4 w-4 mr-2" /> Quay lại danh sách khóa học
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* SEO metadata */}
      {course && (
        <SEO
          title={`${course.name || course.title || "Khóa học"} | Giác Ngộ - Khóa học Phật giáo`}
          description={course.description || "Khóa học Phật giáo trực tuyến - Học tập thông qua bài giảng tương tác và sơ đồ minh họa."}
          ogImage={course.thumbnailUrl || course.imageUrl || course.image}
          ogType="article"
        />
      )}
      
      {/* Actual page content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {showLessonContent && selectedNode && isLessonViewReady ? (
          <>
            {console.log("Rendering LessonContentView with props:", {
              lesson: selectedNode,
              courseId: id || "",
              hasPrev: hasPrevLesson(),
              hasNext: hasNextLesson()
            })}
            <LessonContentView
              lesson={selectedNode}
              courseId={id || ""}
              onPrev={handlePrevLesson}
              onNext={handleNextLesson}
              hasPrev={hasPrevLesson()}
              hasNext={hasNextLesson()}
              onClose={handleCloseLessonContent}
            />
          </>
        ) : showLessonContent && selectedNode ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
            <p className="ml-3">Đang tải nội dung bài học...</p>
          </div>
        ) : (
          <>
            {/* Course Info */}
            {/* padding top 0*/}
            <div className="bg-white rounded-lg shadow-sm p-2 md:p-6 mb-6 pt-0">
              <div className="mb-6 mt-3">
                {/* Course Header */}
                <div className="flex flex-col md:flex-row gap-6 mb-6">
                  <div className="md:w-1/3 lg:w-1/4 flex-shrink-0">
                    {course.image ? (
                      <div className="aspect-video md:aspect-square rounded-xl overflow-hidden shadow-lg">
                        <img
                          src={course.image}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-video md:aspect-square bg-muted rounded-xl flex items-center justify-center">
                        <BookOpen className="h-16 w-16 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <h1 className="text-2xl md:text-3xl font-bold mb-2">
                      {course.name || course.title || "Khóa học Phật giáo"}
                    </h1>

                    <div className="flex flex-wrap gap-2 mb-3">
                      {course.categories?.map((category: string) => (
                        <Badge
                          key={category}
                          variant="outline"
                          className="text-xs"
                        >
                          {category}
                        </Badge>
                      ))}

                      {course.level && (
                        <Badge variant="secondary" className="text-xs">
                          {course.level === "beginner" && t("beginner")}
                          {course.level === "intermediate" && t("intermediate")}
                          {course.level === "advanced" && t("advanced")}
                        </Badge>
                      )}
                    </div>

                    {isPurchased || course.price === 0 || course.is_free === true ? (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <span className="text-sm font-medium">
                            {t("your_progress", { ns: "translation" })}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {calculateProgress()}%
                          </span>
                        </div>
                        <Progress value={calculateProgress()} className="h-2" />

                        <div className="flex flex-wrap gap-2 mt-3">
                          {calculateProgress() === 0 ? (
                            <Button
                              variant="default"
                              onClick={() => {
                                // Tìm node đầu tiên và chuyển hướng
                                if (courseContent?.nodes?.length > 0) {
                                  const firstNodeId = courseContent.nodes[0].id;
                                  console.log('Bắt đầu học - courseId:', id);
                                  console.log('Bắt đầu học - URL sẽ điều hướng đến:', `/courses/${id}/lesson/${firstNodeId}`);
                                  
                                  navigate(
                                    `/courses/${id}/lesson/${firstNodeId}`,
                                  );
                                }
                              }}
                              className="text-primary bg-primary/10"
                            >
                              <Play className="h-4 w-4 mr-2" />
                              {t("start_learning_now", { ns: "translation" })}
                            </Button>
                          ) : calculateProgress() < 100 ? (
                            <Button
                              variant="default"
                              onClick={() => {
                                // Tìm node tiếp theo chưa hoàn thành và chuyển hướng
                                if (courseContent?.nodes) {
                                  // Tìm node đầu tiên chưa hoàn thành
                                  const nextIncompleteNode =
                                    courseContent.nodes.find(
                                      (node: any) => !nodeProgress[node.id],
                                    );
                                  
                                  let targetNodeId = nextIncompleteNode?.id || courseContent.nodes[0].id;
                                  console.log('Tiếp tục học - courseId:', id);
                                  console.log('Tiếp tục học - URL sẽ điều hướng đến:', `/courses/${id}/lesson/${targetNodeId}`);

                                  if (nextIncompleteNode) {
                                    navigate(
                                      `/courses/${id}/lesson/${nextIncompleteNode.id}`,
                                    );
                                  } else {
                                    // Nếu không tìm thấy, chuyển đến node đầu tiên
                                    navigate(
                                      `/courses/${id}/lesson/${courseContent.nodes[0].id}`,
                                    );
                                  }
                                }
                              }}
                              className="text-primary bg-primary/10"
                            >
                              <Play className="h-4 w-4 mr-2" />
                              {t("continue_learning", { ns: "translation" })}
                            </Button>
                          ) : null}

                          {isFullyCompleted() && (
                            <Button
                              onClick={handleShowCertificate}
                              variant="outline"
                              size="sm"
                            >
                              <Award className="h-4 w-4 mr-2" />
                              {t("view_certificate", { ns: "translation" })}
                            </Button>
                          )}

                          {calculateProgress() > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={resetProgress}
                              className="text-destructive hover:text-destructive/90"
                            >
                              <AlertCircle className="h-4 w-4 mr-2" />
                              {t("reset_progress", { ns: "translation" })}
                            </Button>
                          )}

                          {/* Thêm nút chia sẻ khóa học */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleShare}
                          >
                            <Share2 className="h-4 w-4 mr-2" />
                            {t("share", { defaultValue: "Chia sẻ", ns: "translation" })}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-4 p-4 border rounded-lg bg-primary/5">
                        {course.price === 0 || course.is_free === true ? (
                          <>
                            <p className="text-sm mb-2">{t("free_course", { ns: "translation" })}</p>
                            <Button
                              onClick={handleEnrollFreeCourse}
                              variant="default"
                              disabled={purchaseInProgress}
                              className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700"
                            >
                              {purchaseInProgress ? (
                                <span className="flex items-center">
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                  {t("processing", { ns: "translation" })}
                                </span>
                              ) : (
                                <span className="flex items-center">
                                  <Play className="h-4 w-4 mr-2" />
                                  {t("start_learning", { ns: "translation" })}
                                </span>
                              )}
                            </Button>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <p className="text-sm mb-1">
                                  {t("premium_course", { ns: "translation" })}
                                </p>
                                <p className="text-2xl font-bold">
                                  {course.credit_cost}{" "}
                                  {t("credits", { ns: "courses" })}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-muted-foreground mb-1">
                                  {t("your_balance", { ns: "translation" })}
                                </p>
                                <p className="text-lg font-medium">
                                  {userCredits} {t("credits", { ns: "courses" })}
                                </p>
                              </div>
                            </div>

                            <Button
                              onClick={() => setShowPurchaseConfirm(true)}
                              variant="default"
                              disabled={
                                purchaseInProgress ||
                                userCredits < course.credit_cost
                              }
                              className="w-full md:w-auto"
                            >
                              {purchaseInProgress ? (
                                <span className="flex items-center">
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                  {t("processing", { ns: "translation" })}
                                </span>
                              ) : (
                                <span className="flex items-center">
                                  <ShoppingCart className="h-4 w-4 mr-2" />
                                  {t("enroll_for", { ns: "translation" })} {course.credit_cost}{" "}
                                  {t("credits", { ns: "courses" })}
                                </span>
                              )}
                            </Button>

                            {userCredits < course.credit_cost && (
                              <p className="text-sm text-amber-600 mt-2 flex items-center">
                                <AlertCircle className="h-4 w-4 mr-1" />
                                {t("not_enough_credits", { ns: "translation" })}
                              </p>
                            )}
                          </>
                        )}
                        
                        {/* Thêm nút chia sẻ khóa học cho trường hợp chưa mua */}
                        <div className="mt-3">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleShare}
                          >
                            <Share2 className="h-4 w-4 mr-2" />
                            {t("share", { defaultValue: "Chia sẻ", ns: "translation" })}
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="prose prose-lg max-w-none dark:prose-invert">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: processMarkdownContent(
                            course.description || "",
                          ),
                        }}
                      ></div>
                    </div>

                    {course.duration && (
                      <div className="flex items-center text-sm text-muted-foreground mt-3">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>
                          {t("estimated_duration", { ns: "translation" })}: {course.duration}
                        </span>
                      </div>
                    )}

                    {course.estimated_time && (
                      <div className="flex items-center text-sm text-muted-foreground mt-3">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>
                          {t("estimated_time", { ns: "translation" })}:{" "}
                          {decodeUnicodeString(course.estimated_time)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Course Content */}
                {(isPurchased || course.price === 0 || course.is_free === true) && courseContent && (
                  <div className="mt-8">
                    <Tabs defaultValue="content" className="w-full">
                      <TabsList className="mb-6">
                        <TabsTrigger value="content">
                          {t("course_content", { ns: "translation" })}
                        </TabsTrigger>
                        {course.course_materials && (
                          <TabsTrigger value="materials">
                            {t("course_materials", { ns: "translation" })}
                          </TabsTrigger>
                        )}
                        <TabsTrigger value="ai">{t("ai_assistant", { ns: "translation" })}</TabsTrigger>
                      </TabsList>

                      <TabsContent value="content">
                        <div className="mb-4 flex items-center justify-between">
                          <h2 className="text-xl font-semibold">
                            {t("lessons", { ns: "courses" })}
                          </h2>

                          {/* Desktop view mode toggle */}
                          <div className="hidden md:flex items-center space-x-2">
                            <Button
                              variant={
                                viewMode === "card" ? "default" : "outline"
                              }
                              size="sm"
                              onClick={() => setViewMode("card")}
                              className="w-9 p-0"
                            >
                              <Layout className="h-4 w-4" />
                            </Button>
                            <Button
                              variant={
                                viewMode === "list" ? "default" : "outline"
                              }
                              size="sm"
                              onClick={() => setViewMode("list")}
                              className="w-9 p-0"
                            >
                              <List className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Render nodes in the selected view mode */}
                        {viewMode === "card" ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {courseContent && courseContent.nodes ? (
                              courseContent.nodes.map((node: any) => (
                                <NodeCard
                                  key={node.id}
                                  node={node}
                                  isActive={node.id === activeNodeId}
                                  onClick={() => handleViewLesson(node)}
                                  isCompleted={nodeProgress[node.id] === true}
                                />
                              ))
                            ) : courseContent && Array.isArray(courseContent) ? (
                              // Trường hợp courseContent là mảng
                              (courseContent as any[]).map((node: any) => (
                                <NodeCard
                                  key={node.id}
                                  node={node}
                                  isActive={node.id === activeNodeId}
                                  onClick={() => handleViewLesson(node)}
                                  isCompleted={nodeProgress[node.id] === true}
                                />
                              ))
                            ) : (
                              <div className="col-span-3 p-4 border rounded-md bg-red-50 text-red-500">
                                <h3 className="font-semibold mb-2">
                                  Khóa học đang gặp vấn đề kỹ thuật
                                </h3>
                                <p>
                                  Vui lòng liên hệ tech@giacngo.org để báo cáo lỗi
                                  này.
                                </p>
                                <p className="text-xs mt-2">
                                  DEBUG: courseContent type: {typeof courseContent}, has nodes: {courseContent && typeof courseContent === 'object' ? String(!!courseContent.nodes) : 'N/A'}
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {courseContent && courseContent.nodes ? (
                              courseContent.nodes.map((node: any) => (
                                <div
                                  key={node.id}
                                  className={`p-3 rounded-md cursor-pointer transition-colors ${
                                    node.id === activeNodeId
                                      ? "bg-primary/10 text-primary border-l-4 border-primary"
                                      : "hover:bg-slate-50 dark:hover:bg-slate-800"
                                  }`}
                                  onClick={() => handleViewLesson(node)}
                                >
                                  <div className="flex items-start gap-2">
                                    <div className="mt-0.5">
                                      {nodeProgress[node.id] ? (
                                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                                      ) : (
                                        <Circle className="h-5 w-5 text-slate-300" />
                                      )}
                                    </div>
                                    <div>
                                      <h3 className="font-medium">
                                        {node.name}
                                      </h3>
                                      {node.description && (
                                        <p className="text-sm text-muted-foreground line-clamp-1">
                                          {node.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : courseContent && Array.isArray(courseContent) ? (
                              // Trường hợp courseContent là mảng
                              (courseContent as any[]).map((node: any) => (
                                <div
                                  key={node.id}
                                  className={`p-3 rounded-md cursor-pointer transition-colors ${
                                    node.id === activeNodeId
                                      ? "bg-primary/10 text-primary border-l-4 border-primary"
                                      : "hover:bg-slate-50 dark:hover:bg-slate-800"
                                  }`}
                                  onClick={() => handleViewLesson(node)}
                                >
                                  <div className="flex items-start gap-2">
                                    <div className="mt-0.5">
                                      {nodeProgress[node.id] ? (
                                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                                      ) : (
                                        <Circle className="h-5 w-5 text-slate-300" />
                                      )}
                                    </div>
                                    <div>
                                      <h3 className="font-medium">
                                        {node.name}
                                      </h3>
                                      {node.description && (
                                        <p className="text-sm text-muted-foreground line-clamp-1">
                                          {node.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="p-4 border rounded-md bg-red-50 text-red-500">
                                <h3 className="font-semibold mb-2">
                                  Không thể hiển thị nội dung khóa học
                                </h3>
                                <p>
                                  Vui lòng liên hệ tech@giacngo.org để báo cáo lỗi
                                  này.
                                </p>
                                <p className="text-xs mt-2">
                                  DEBUG: courseContent type: {typeof courseContent}, has nodes: {courseContent && typeof courseContent === 'object' ? String(!!courseContent.nodes) : 'N/A'}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </TabsContent>

                      {course.course_materials && (
                        <TabsContent value="materials">
                          <div className="space-y-4">
                            <h2 className="text-xl font-semibold">
                              {t("course_materials", { ns: "translation" })}
                            </h2>
                            {/* Hiển thị tài liệu khóa học tại đây */}
                          </div>
                        </TabsContent>
                      )}

                      <TabsContent value="ai">
                        <div className="p-4 border rounded-lg">
                          <h2 className="text-xl font-semibold mb-4">
                            {t("ai_assistant", { ns: "translation" })}
                          </h2>
                          
                          {/* Hiển thị widget chat AI trực tiếp trong tab */}
                          {course && (
                            <ContentChatWidget 
                              contentType="course"
                              contentId={id || ""}
                              contentName={course.name}
                              contentData={course.description || ""}
                              contentDescription={course.description || ""}
                              skipSaveHistory={true}
                              suggestedQuestions={[
                                t("default_questions.explain_concepts", { ns: "courses" }),
                                t("default_questions.practical_application", { ns: "courses" }),
                                t("default_questions.related_concepts", { ns: "courses" })
                              ]}
                            />
                          )}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Dialog xác nhận mua khóa học */}
        <Dialog open={showPurchaseConfirm} onOpenChange={setShowPurchaseConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("confirm_purchase")}</DialogTitle>
              <DialogDescription>
                {t("confirm_purchase_description", {
                  courseName: course?.name,
                  price: course?.credit_cost,
                })}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="flex justify-between items-center mb-2">
                <span>{t("your_balance")}</span>
                <span className="font-semibold">
                  {userCredits} {t("credits", { ns: "courses" })}
                </span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span>{t("coursePrice")}</span>
                <span className="font-semibold text-orange-500">
                  {course?.credit_cost} {t("credits", { ns: "courses" })}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span>{t("remainingCredits")}</span>
                <span
                  className={`font-semibold ${userCredits >= (course?.credit_cost || 0) ? "text-green-500" : "text-red-500"}`}
                >
                  {userCredits - (course?.credit_cost || 0)}{" "}
                  {t("credits", { ns: "courses" })}
                </span>
              </div>
            </div>

            {userCredits < (course?.credit_cost || 0) && (
              <div className="bg-red-50 p-3 rounded-md text-red-600 text-sm">
                {t("notEnoughCredits")}
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowPurchaseConfirm(false)}
              >
                {t("common:cancel")}
              </Button>
              <Button
                variant="default"
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                onClick={handleConfirmPurchase}
                disabled={
                  purchaseInProgress || userCredits < (course?.credit_cost || 0)
                }
              >
                {purchaseInProgress ? (
                  <span className="flex items-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {t("processing", { ns: "translation" })}
                  </span>
                ) : (
                  t("confirm_purchase")
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog chia sẻ khóa học */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("share_course", { defaultValue: "Chia sẻ khóa học", ns: "translation" })}</DialogTitle>
              <DialogDescription>
                {t("share_course_description", { defaultValue: "Chia sẻ khóa học này với bạn bè của bạn", ns: "translation" })}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <h3 className="font-medium mb-2">{course?.name}</h3>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {course?.description}
              </p>

              <div className="flex flex-col space-y-4">
                <div>
                  <Label htmlFor="share-url">Link chia sẻ</Label>
                  <div className="flex mt-1">
                    <Input 
                      id="share-url" 
                      value={shareableLink} 
                      readOnly 
                      className="flex-1 rounded-r-none"
                    />
                    <Button 
                      variant="outline" 
                      className="rounded-l-none"
                      onClick={() => {
                        navigator.clipboard.writeText(shareableLink);
                        toast({
                          title: "Đã sao chép",
                          description: "Link đã được sao chép vào clipboard"
                        });
                      }}
                    >
                      Sao chép
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowShareDialog(false)}>
                {t("common:close", { defaultValue: "Đóng" })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showCertificate} onOpenChange={setShowCertificate}>
          {/* ... existing dialogs ... */}
        </Dialog>
        
        {/* Thêm CoursePdfExport component nếu có nội dung khóa học */}
        {isPurchased && courseContent && ((courseContent.nodes && courseContent.nodes.length > 0) || 
          (Array.isArray(courseContent) && courseContent.length > 0)) && (
          <CoursePdfExport 
            courseId={id || ""}
            courseTitle={course?.title || ""}
            lessons={courseContent.nodes || courseContent}
            className="mt-8"
          />
        )}
      </div>
    </div>
  );
};

export default CourseDetailPage;
